import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import compact from 'lodash/compact'

import SearchBar, { searchProps } from 'components/SearchBar'
import UniversalLink from 'components/UniversalLink'

import IconChevronRight from '@anvilco-pkg/ui/components/Icon/IconChevronRight'

import baseTheme from 'theme'
import media from 'media'

import { NAV_BAR_HEIGHT } from 'consts'
import Filter from 'components/blog/PostGrid/Filter'

export const CLASS_NAME = 'sidebar'

const Container = styled(Box).attrs({
  width: [1, 1, 0.4],
  className: CLASS_NAME,
})`
  ${media.desktop`
    border-right: 2px solid ${baseTheme.colors.black};
    max-width: 380px;
    min-width: 300px;
  `}
`

const SidebarHeader = styled(Box).attrs({
  width: [1],
  pr: [0, 0, 20, 40, 50],
  pt: [0, 20, 0],
})``

const Eyebrow = styled.div`
  font-weight: ${baseTheme.fontWeights.lightest};
  color: ${baseTheme.colors.black};
`

const TitleH1 = styled.h1.withConfig({
  shouldForwardProp: (prop) => ['applyEyebrowStyles'].indexOf(prop) === -1,
})`
  ${({ applyEyebrowStyles }) =>
    applyEyebrowStyles &&
    `
    margin-top: 6px;
  `}
  font-size: ${baseTheme.fontSizes[240]}px;

  ${media.desktop`
    font-size: ${baseTheme.fontSizes[360]}px;
  `}

  ${media.giant`
    font-size: ${baseTheme.fontSizes[520]}px;
  `}
`

const TitleH2 = styled.h2.withConfig({
  shouldForwardProp: (prop) => ['applyEyebrowStyles'].indexOf(prop) === -1,
})`
  ${({ applyEyebrowStyles }) =>
    applyEyebrowStyles &&
    `
    margin-top: 6px;
  `}
  font-size: ${baseTheme.fontSizes[120]}px;

  ${media.tablet`
    font-size: ${baseTheme.fontSizes[240]}px;
  `}
`

const Description = styled.div`
  margin: 32px 0 24px;
`

const Subtitle = styled.div.withConfig({
  shouldForwardProp: (prop) => ['isFilterTitle'].indexOf(prop) === -1,
})`
  font-size: ${baseTheme.fontSizes[30]}px;
  font-weight: ${baseTheme.fontWeights.lightest};

  margin-bottom: 6px;

  ${({ isFilterTitle }) =>
    isFilterTitle &&
    `
    display: none;
  `}

  ${media.desktop`
    display: block;
  `}
`

const Menu = styled(Flex).attrs({
  flexDirection: 'column',
  pr: [0, 0, 20, 40, 50],
})`
  overflow: auto;
  position: sticky;
  top: ${NAV_BAR_HEIGHT + 20}px;
  height: 100%;
  max-height: calc(100vh - ${NAV_BAR_HEIGHT}px);

  ::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const MenuItem = styled(Flex)
  .attrs({
    width: [1],
    justifyContent: 'space-between',
    my: 10,
    alignItems: 'center',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isActive'].indexOf(prop) === -1,
  })`
  display: none;

  ${({ isActive }) =>
    isActive &&
    `
    font-weight: ${baseTheme.fontWeights.bold};
  `}

  ${media.desktop`
    display: flex;
  `}

  &:hover {
    cursor: pointer;
    color: ${({ theme }) =>
      theme?.textColors?.linkHover || baseTheme.textColors.linkHover};    
  font-weight: ${baseTheme.fontWeights.bold};

    svg,
    path {
      fill: ${({ theme }) =>
        theme?.textColors?.linkHover || baseTheme.textColors.linkHover};    
      font-weight: ${baseTheme.fontWeights.bold};
    }
  }
`

const SearchAndFilterContainer = styled(Flex)
  .attrs({
    flexDirection: ['column', 'row'],
    alignItems: 'center',
    mb: [0, 0, 10],
  })
  .withConfig({
    shouldForwardProp: (prop) => ['numChildren'].indexOf(prop) === -1,
  })`
  & > * {
    width: 100%;
  }

  ${media.tablet`
    & > * {
      width: ${({ numChildren }) => 100 / numChildren}%;

      ${({ numChildren }) =>
        numChildren === 2 &&
        `
        :first-child {
          margin-right: 12px;
        }

        :last-child {
          margin-left: 12px;
        }
      `}
    }
  `}

  ${media.desktop`
    & > * {
      width: 100%;
    }
  `}
`

const StyledFilter = styled(Filter)`
  ${media.desktop`
    display: none;
  `}
`

const StyledLink = styled(UniversalLink)`
  display: none;
  margin-top: 10px;

  ${media.desktop`
    display: block;
  `}
`

const SidebarTitle = ({ isPageTitle, applyEyebrowStyles, children }) => {
  const Component = isPageTitle ? TitleH1 : TitleH2

  return (
    <Component applyEyebrowStyles={applyEyebrowStyles}>{children}</Component>
  )
}

const Sidebar = ({
  renderHeader,
  eyebrow,
  title,
  description,
  search,
  filterable,
  menuItems,
  selectedItem,
  isPageTitle,
  filterTitle,
  subtitle,
  footerLink,
  ...others
}) => {
  return (
    <Container {...others}>
      {renderHeader ? (
        renderHeader()
      ) : (
        <SidebarHeader>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <SidebarTitle isPageTitle={isPageTitle} applyEyebrowStyles={eyebrow}>
            {title}
          </SidebarTitle>
          <Description>{description}</Description>
        </SidebarHeader>
      )}
      <Menu>
        {subtitle && (
          <Subtitle isFilterTitle={filterTitle}>{subtitle}</Subtitle>
        )}
        <SearchAndFilterContainer
          numChildren={compact([search, filterable]).length}
        >
          {search && (
            <SearchBar
              placeholder={search.placeholder}
              onSearch={search.onSearch}
              results={search.results}
              query={search.query}
              localSearchData={search.localSearchData}
              searchOptions={{
                idsOnly: true,
                idFieldName: 'slug',
              }}
            />
          )}
          {filterable && (
            <StyledFilter
              selectedValue={selectedItem}
              values={menuItems.map((item) => item.value)}
              onClicksArray={menuItems.map((item) => item.onClick)}
              filterTitle={filterTitle}
            />
          )}
        </SearchAndFilterContainer>
        {menuItems.map(({ key, value, onClick }) => (
          <MenuItem
            key={key}
            onClick={onClick}
            isActive={selectedItem === value}
          >
            {value}
            <IconChevronRight />
          </MenuItem>
        ))}
        {footerLink && (
          <StyledLink to={footerLink?.to || footerLink?.href}>
            {footerLink.text}
          </StyledLink>
        )}
      </Menu>
    </Container>
  )
}

const subMenuItemShape = {
  key: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
}

export const menuItemShape = {
  ...subMenuItemShape,
  subMenuItems: PropTypes.arrayOf(PropTypes.shape(subMenuItemShape)),
}

Sidebar.propTypes = {
  renderHeader: PropTypes.func,
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  isPageTitle: PropTypes.bool,
  description: PropTypes.string.isRequired,
  ...searchProps,
  filterable: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.shape(menuItemShape)).isRequired,
}

export default Sidebar
